import React from "react";

import { motion } from "framer-motion";

import { ArrowRight } from "lucide-react";

import TiltedCard from "../../animations/TiltedCard";
import SpotlightCard from "../../animations/SpotlightCard";
import GlareHover from "../../animations/GlareHover";

const PolicyCard = ({
  icon: Icon,
  title,
  description,
  points = [],
  index,
}) => {
  return (
    <TiltedCard containerHeight="100%" containerWidth="100%">
      <SpotlightCard
        tailwindColor="#E67000"
        alpha={0.1}
        radius={280}
        className="h-full rounded-2xl"
      >
        <GlareHover
          borderRadius="16px"
          borderColor="rgba(249,115,22,.12)"
          glareColor="#E67000"
          glareOpacity={0.1}
          className="h-full"
        >
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{
              duration: 0.45,
              delay: index * 0.08,
            }}
            whileHover={{
              y: -3,
            }}
            className="
              group
              relative
              flex
              h-full
              flex-col
              overflow-hidden
              rounded-2xl
              border
              p-4
              transition-all
              duration-300
              hover:border-orange-500/40
              sm:p-5
            "
            style={{
              backgroundColor: "var(--card-bg)",
              borderColor: "var(--border-primary)",
              color: "var(--text-primary)",
            }}
          >
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,#f9731620,transparent_72%)] opacity-0 transition-opacity duration-500 group-hover:opacity-100" />

            <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-orange-500/50 to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />

            <div className="relative z-10 flex h-full flex-col">
              <motion.div
                whileHover={{
                  rotate: 8,
                  scale: 1.05,
                }}
                transition={{ duration: 0.25 }}
                className="
                  mb-3
                  inline-flex
                  h-10
                  w-10
                  items-center
                  justify-center
                  rounded-xl
                  border
                  sm:h-11
                  sm:w-11
                "
                style={{
                  borderColor:
                    "color-mix(in srgb, var(--brand-primary) 30%, transparent)",
                  backgroundColor:
                    "color-mix(in srgb, var(--brand-primary) 10%, transparent)",
                }}
              >
                <Icon
                  size={20}
                  style={{
                    color: "var(--brand-primary)",
                  }}
                />
              </motion.div>

              <h3
                className="
                  text-lg
                  font-bold
                  leading-snug
                  transition-colors
                  duration-300
                  group-hover:text-orange-400
                  sm:text-xl
                "
                style={{
                  color: "var(--text-primary)",
                }}
              >
                {title}
              </h3>

              <p
                className="
                  mt-2
                  text-sm
                  leading-6
                  sm:text-[15px]
                  sm:leading-7
                "
                style={{
                  color: "var(--text-muted)",
                }}
              >
                {description}
              </p>

              {points.length > 0 && (
                <div className="mt-4 space-y-2.5">
                  {points.map((point, i) => (
                    <motion.div
                      key={i}
                      initial={{
                        opacity: 0,
                        x: -12,
                      }}
                      whileInView={{
                        opacity: 1,
                        x: 0,
                      }}
                      viewport={{ once: true }}
                      transition={{
                        duration: 0.35,
                        delay: i * 0.05,
                      }}
                      whileHover={{
                        x: 3,
                      }}
                      className="group/item flex items-start gap-2.5"
                    >
                      <div
                        className="
                          mt-0.5
                          flex
                          h-5
                          w-5
                          flex-shrink-0
                          items-center
                          justify-center
                          rounded-full
                          transition-all
                          duration-300
                          group-hover/item:bg-orange-500/20
                        "
                        style={{
                          backgroundColor:
                            "color-mix(in srgb, var(--brand-primary) 10%, transparent)",
                        }}
                      >
                        <ArrowRight
                          size={11}
                          className="transition-transform duration-300 group-hover/item:translate-x-0.5"
                          style={{
                            color: "var(--brand-primary)",
                          }}
                        />
                      </div>

                      <span
                        className="
                          text-[14px]
                          leading-6
                          transition-colors
                          duration-300
                          sm:text-sm
                          sm:leading-6
                        "
                        style={{
                          color: "var(--text-secondary)",
                        }}
                      >
                        {point}
                      </span>
                    </motion.div>
                  ))}
                </div>
              )}

              <div className="flex-grow" />

              <motion.div
                initial={{ scaleX: 0 }}
                whileInView={{ scaleX: 1 }}
                viewport={{ once: true }}
                transition={{
                  duration: 0.6,
                  delay: 0.2,
                }}
                className="mt-5 h-[2px] origin-left rounded-full bg-gradient-to-r from-orange-500 via-orange-400/40 to-transparent"
              />
            </div>
          </motion.div>
        </GlareHover>
      </SpotlightCard>
    </TiltedCard>
  );
};

export default PolicyCard;